import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API, fileUrl, formatError } from "@/lib/api";
import { ShareButtons } from "@/components/ShareButtons";
import { MockupPreview } from "@/components/merch/MockupPreview";
import { ProductPicker, TeeOptions } from "@/components/merch/ProductOptions";
import { ShippingForm } from "@/components/merch/ShippingForm";
import { useMockups } from "@/components/merch/useMockups";

const MockupShare = ({ share, sharing, onShare }) => share ? (
  <div className="mt-3"><ShareButtons id={share.share_id} link={`${API}/sm/${share.share_id}`}
    text={`${share.pet_name} on a ${share.product_name}! Made with Cutie Parlor`} title="Share this mockup" /></div>
) : (
  <button type="button" onClick={onShare} disabled={sharing} data-testid="merch-share-mockup-button"
    className="mt-3 flex items-center gap-2 rounded-full border border-[#1E1B26] px-4 py-1.5 text-sm font-bold hover:bg-[#1E1B26] hover:text-white transition-colors disabled:opacity-60">
    {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share this mockup
  </button>
);

export const MerchDialog = ({ open, onOpenChange, generation }) => {
  const [items, setItems] = useState([]);
  const [type, setType] = useState("mug");
  const [size, setSize] = useState("M");
  const [color, setColor] = useState("White");
  const [active, setActive] = useState(0);
  const [share, setShare] = useState(null);
  const [sharing, setSharing] = useState(false);
  const tee = type === "tshirt";
  const opts = { type, size: tee ? size : null, color: tee ? color : null };
  const genId = generation ? generation.generation_id : null;
  const mock = useMockups(open, genId, opts);

  useEffect(() => { if (open && !items.length) api.get("/merch").then((r) => setItems(r.data)); }, [open, items.length]);
  useEffect(() => { setActive(0); setShare(null); }, [type, size, color]);

  const shareMockup = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/mockup-shares", { generation_id: genId, product_type: type, size: opts.size, color: opts.color, mockup_url: mock.urls[active] });
      setShare(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSharing(false);
    }
  };

  const checkout = async (form) => {
    try {
      const { data } = await api.post("/orders", { ...form, generation_id: genId, product_type: type, size: opts.size, color: opts.color, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
      return true;
    } catch (err) {
      toast.error(formatError(err));
      return false;
    }
  };

  const current = items.find((i) => i.id === type);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="merch-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Put it on something</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <MockupPreview mock={mock} active={active} onPick={setActive} type={type} src={fileUrl(generation?.output_path)} />
            {!mock.loading && mock.urls.length > 0 && <MockupShare share={share} sharing={sharing} onShare={shareMockup} />}
            <ProductPicker items={items} type={type} onChange={setType} />
            {tee && <TeeOptions colors={current?.colors} size={size} color={color} onSize={setSize} onColor={setColor} />}
          </div>
          <ShippingForm price={current?.price} onSubmit={checkout} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
